// ./src/components/features/Connectors/ConfigEditors/TelegramConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Anchor,
  Alert,
  Code,
  Switch,
  Group,
  Box,
  List,
  Button,
} from "@mantine/core";
import {
  IconInfoCircle,
  IconBrandTelegram,
  IconExternalLink,
} from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { TelegramConnectorConfig } from "@/types";

export interface TelegramConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: TelegramConnectorConfig = {
  botToken: "",
  chatId: "",
  showMetadata: true,
  showFiles: true,
  showViewButton: true,
  headerPattern: "📬 New submission: {{formName}}",
};

/**
 * Parse and validate Telegram connector configuration
 */
const parseTelegramConfig = (jsonString: string): TelegramConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

/**
 * Validate bot token format
 */
const isValidBotToken = (token: string): boolean => {
  return /^\d+:[A-Za-z0-9_-]+$/.test(token);
};

export const TelegramConnectorConfigEditor: React.FC<
  TelegramConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<TelegramConnectorConfig>(() =>
    parseTelegramConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseTelegramConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: TelegramConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleBotTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, botToken: e.target.value });
  };

  const handleChatIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, chatId: e.target.value });
  };

  const handleHeaderPatternChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, headerPattern: e.target.value });
  };

  const handleShowMetadataChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, showMetadata: e.currentTarget.checked });
  };

  const handleShowFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, showFiles: e.currentTarget.checked });
  };

  const handleShowViewButtonChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, showViewButton: e.currentTarget.checked });
  };

  // Open getUpdates URL in new tab
  const handleGetChatId = () => {
    const url = `https://api.telegram.org/bot${config.botToken}/getUpdates`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Check if bot token is valid for enabling the button
  const hasValidBotToken = isValidBotToken(config.botToken);

  // Validate bot token format for error display
  const botTokenError = (() => {
    if (!config.botToken) return undefined;
    if (!isValidBotToken(config.botToken)) {
      return "Invalid bot token format";
    }
    return undefined;
  })();

  return (
    <Stack gap="md">
      {/* Setup Instructions */}
      <Alert
        icon={<IconBrandTelegram size={16} />}
        color="blue"
        variant="light"
      >
        <Text size="sm" fw={500} mb="xs">
          Setup Instructions:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Message{" "}
            <Anchor
              href="https://t.me/BotFather"
              target="_blank"
              rel="noopener noreferrer"
            >
              @BotFather
            </Anchor>{" "}
            on Telegram
          </List.Item>
          <List.Item>
            Send <Code>/newbot</Code> and follow the prompts
          </List.Item>
          <List.Item>Copy the bot token provided</List.Item>
          <List.Item>Start a chat with your bot or add it to a group</List.Item>
          <List.Item>
            Enter the token below, then click "Get Chat ID" to find your Chat ID
          </List.Item>
        </List>
      </Alert>

      {/* Bot Token Field */}
      <TextInput
        label="Bot Token"
        placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
        description="Token from @BotFather"
        value={config.botToken}
        onChange={handleBotTokenChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
        error={botTokenError}
      />

      {/* Chat ID Field with Helper Button */}
      <Stack gap="xs">
        <TextInput
          label="Chat ID"
          placeholder="-1001234567890 or @channelusername"
          description="User ID, group ID (negative number), or @channelusername"
          value={config.chatId}
          onChange={handleChatIdChange}
          disabled={disabled}
          required
          size="md"
        />
        <Button
          variant="light"
          size="xs"
          leftSection={<IconExternalLink size={14} />}
          onClick={handleGetChatId}
          disabled={disabled || !hasValidBotToken}
          style={{ alignSelf: "flex-start" }}
        >
          Get Chat ID
        </Button>
        <Text size="xs" c="dimmed">
          Opens Telegram API in a new tab. Look for{" "}
          <Code>"chat":{"{"}"id":...</Code> in the response.
        </Text>
      </Stack>

      {/* Header Pattern Field */}
      <TextInput
        label="Header Pattern"
        placeholder="📬 New submission: {{formName}}"
        description="Customize the message header"
        value={config.headerPattern || "📬 New submission: {{formName}}"}
        onChange={handleHeaderPatternChange}
        disabled={disabled}
        size="md"
      />

      {/* Toggle Options */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Message Options
        </Text>
        <Stack gap="sm">
          <Group justify="space-between">
            <div>
              <Text size="sm">Show Metadata</Text>
              <Text size="xs" c="dimmed">
                Display submission ID and timestamp
              </Text>
            </div>
            <Switch
              checked={config.showMetadata !== false}
              onChange={handleShowMetadataChange}
              disabled={disabled}
            />
          </Group>

          <Group justify="space-between">
            <div>
              <Text size="sm">Show Files</Text>
              <Text size="xs" c="dimmed">
                List attached files with sizes
              </Text>
            </div>
            <Switch
              checked={config.showFiles !== false}
              onChange={handleShowFilesChange}
              disabled={disabled}
            />
          </Group>

          <Group justify="space-between">
            <div>
              <Text size="sm">Show View Button</Text>
              <Text size="xs" c="dimmed">
                Include "View Submission" button
              </Text>
            </div>
            <Switch
              checked={config.showViewButton !== false}
              onChange={handleShowViewButtonChange}
              disabled={disabled}
            />
          </Group>
        </Stack>
      </Box>

      {/* Placeholders Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Available placeholders for header:
        </Text>
        <Text size="xs" component="div">
          <Code>{"{{formName}}"}</Code> — Form name
          <br />
          <Code>{"{{formId}}"}</Code> — Form UUID
          <br />
          <Code>{"{{requestId}}"}</Code> — Submission ID
          <br />
          <Code>{"{{date}}"}</Code> — Date (YYYY-MM-DD)
        </Text>
      </Alert>

      {/* Info Note */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Messages will include all visible form fields. Hidden fields and
          security fields (honeypot, reCAPTCHA) are automatically excluded.
        </Text>
      </Alert>
    </Stack>
  );
};
